import {
  useQuery,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query'
import to from 'await-to-js'
import {
  DataTable,
  DataTableProvider,
  useDataTableContext,
} from 'components/DataTable'
import NotesModal from 'components/Modals/NotesModal'
import dialogConfirm from 'components/dialogConfirm'
import { INote } from 'models/contact_companies'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import useApiService from 'services/api'
import { ColumnsStoreProvider } from 'stores/ColumnsStore/ColumnsStoreProvider'
import styled from 'styled-components'
import { getTableQueryKey } from '../../../utils/getTableQueryKey'
import { dataTableSortingStoreRepo } from '../../DataTable/DataTableSorting/DataTableSortingStore'
import { IColumnVisibility } from '../../DataTable/types'
import {
  filterStoreRepo,
  useFilterParams,
} from 'components/Filters/FilterStore'
import { FilterIdentifier } from 'models/saved_view'
import { useNotesTableColumns } from './useNotesTableColumns'
import * as S from '../CommonTable.styles'
import { ColumnSelectorRecipient } from 'components/Modals/ColumnModal/ColumnModal'
import { FilterChips } from 'components/Filters/components/FilterChips/FilterChips'

type SortableFields = (keyof INote | string)[]

const sortableFields: SortableFields = ['created', 'modified']

const PAGE_SIZE = 100

interface NotesTableProps {
  setTotalRowsCount?: (count: number) => void
  enablePagination?: boolean
  disableUrlPagination?: boolean
  chainId: number | undefined
  contactCompanyId: number | undefined
  doorProxyId?: number | undefined
  tableKey: string
  filterIdentifier: FilterIdentifier
  notesFilterset?: React.ReactNode
  showDoorColumns?: boolean
  noteType?: 'company' | 'door'
}

function NotesTableComponent(props: NotesTableProps) {
  const queryClient = useQueryClient()
  const [activeNote, setActiveNote] = useState<{
    note: INote
    isViewing?: boolean
  } | null>(null)
  const { sorting } = dataTableSortingStoreRepo.getStore(props.tableKey)()
  const {
    methods: { clearSelectedRows, setTotalRowsInBackend },
  } = useDataTableContext()

  const notesFilterStore = filterStoreRepo.getStore(props.filterIdentifier)
  const { page, setPage } = notesFilterStore()
  const filtersAsParams = useFilterParams(notesFilterStore)

  const api = useApiService()

  const sortParams = useMemo(() => {
    const params: Record<string, unknown> = {}

    if (sorting?.length) {
      params['sort'] = sorting[0].desc ? '-' + sorting[0]?.id : sorting[0]?.id
    }

    return params
  }, [sorting])

  const filterAndSortParams = useMemo(
    () => ({
      ...filtersAsParams,
      ...sortParams,
    }),
    [filtersAsParams, sortParams]
  )

  const TABLE_QUERY_KEY = getTableQueryKey({
    tableKey: props.tableKey,
    filterParams: filterAndSortParams,
    page: page,
  })

  const handleNoteDelete = async (noteId: number) => {
    if (
      await dialogConfirm(
        <>
          Are you sure you want to delete this <b>note</b>? This action cannot
          be undone.
        </>,
        'Delete Note?',
        '',
        { confirmLabel: 'Delete' }
      )
    ) {
      const [err] = await to(api.deleteNote(noteId))
      if (!err) {
        toast.success('Note deleted successfully')
        refetch()
        if (props.contactCompanyId) {
          queryClient.invalidateQueries({
            queryKey: ['company', props.contactCompanyId],
          })
        }
        if (props.doorProxyId) {
          queryClient.invalidateQueries({
            queryKey: ['door_proxy', props.doorProxyId],
          })
        }
      }
    }
  }

  const columns = useNotesTableColumns({
    setActiveNote,
    handleNoteDelete,
    showDoorColumns: props.showDoorColumns,
  })

  const { isFetching, data, refetch } = useQuery({
    queryKey: ['notes-table', ...TABLE_QUERY_KEY],
    queryFn: async ({ signal }) => {
      clearSelectedRows()

      if (!props.chainId && !props.contactCompanyId && !props.doorProxyId)
        return {
          results: [],
          count: 0,
        }

      const params = {
        ...filterAndSortParams,
        page: page,
        limit: PAGE_SIZE,
      }

      const res = await api.getNotes(
        props.chainId,
        props.contactCompanyId,
        params,
        signal,
        props.doorProxyId,
        props.noteType
      )

      if (typeof res.count === 'number') {
        props.setTotalRowsCount?.(res.count)
      }
      return res
    },
    enabled: !!(props.chainId || props.contactCompanyId || props.doorProxyId),
    placeholderData: keepPreviousData,
  })

  useEffect(() => {
    if (typeof data?.count === 'number') {
      props.setTotalRowsCount?.(data.count)
      setTotalRowsInBackend?.(data.count)
    }
  }, [data?.count, props.setTotalRowsCount, setTotalRowsInBackend])

  return (
    <TableContainer>
      <DataTable
        key={props.tableKey}
        tableKey={props.tableKey}
        tableHeader={
          props.notesFilterset ? (
            <S.SearchContainer>
              <S.SearchContainerFilters>
                <S.TableSearchContainer>
                  <div className="flex-1"></div>
                </S.TableSearchContainer>

                <S.TableButtonsContainer>
                  {props.notesFilterset}
                  <ColumnSelectorRecipient tableKey={props.tableKey} />
                </S.TableButtonsContainer>
              </S.SearchContainerFilters>
              <FilterChips
                identifier={props.filterIdentifier}
                store={notesFilterStore}
                showActive
                clearAllButton
              />
            </S.SearchContainer>
          ) : undefined
        }
        loading={isFetching}
        data={data?.results ?? []}
        columns={columns}
        sortableFields={sortableFields}
        virtualizeRows={false}
        isPaginationEnabled={props.enablePagination}
        expandColAccessor={'expanded-row' as any}
        paginationOptions={{
          totalRows: data?.count ?? 0,
          pageSize: PAGE_SIZE,
          setPage: setPage,
          page: page,
          isPaginationLoading: isFetching,
        }}
        onRowDoubleClick={(row) => {
          const note = row.original
          setActiveNote({ note, isViewing: true })
        }}
      />

      {activeNote && (
        <NotesModal
          chainId={props.chainId}
          contactCompanyId={props.contactCompanyId}
          doorProxyId={props.doorProxyId}
          show={!!activeNote}
          noteToEdit={activeNote.note}
          viewMode={activeNote.isViewing}
          onSubmitSuccess={() => {
            refetch()
          }}
          onEditMode={() => {
            setActiveNote((prev) =>
              prev ? { ...prev, isViewing: false } : null
            )
          }}
          handleClose={() => {
            setActiveNote(null)
          }}
        />
      )}
    </TableContainer>
  )
}

export function NotesTable(
  props: NotesTableProps & {
    defaultColumnVisibility?: IColumnVisibility<any>
    notesFilterset?: React.ReactNode
    savedPresetsIdentifier?: FilterIdentifier
  }
) {
  const {
    tableKey,
    defaultColumnVisibility,
    notesFilterset,
    savedPresetsIdentifier,
    ...rest
  } = props

  return (
    <ColumnsStoreProvider
      tableKey={tableKey}
      savedPresetsIdentifier={savedPresetsIdentifier || 'NOTES_TABLE'}
      defaultColumnVisibility={defaultColumnVisibility}
      preferredGroupOrder={['Note Details', 'Door Details', 'Dates', 'Actions']}
    >
      <DataTableProvider tableKey={tableKey}>
        <NotesTableComponent
          tableKey={tableKey}
          {...rest}
          notesFilterset={notesFilterset}
        />
      </DataTableProvider>
    </ColumnsStoreProvider>
  )
}

const TableContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex-shrink: 1;
  width: 100%;
  min-height: 400px;
`
